import { z } from 'zod';
import { QualitySchema, ResolutionSchema } from '../ingest.js';

/**
 * Tag time-series contract (CLAUDE.md §10 `GET /api/tags/:id/series?from&to&res`, §11.1
 * `/trends`).
 *
 * Single source of truth for the time-series explorer payload — imported by the Worker
 * route, its tests, and the React `/trends` screen. One tag per request; the explorer
 * fetches several tags and overlays them (multi-tag + driver overlay) client-side.
 *
 * `resolution` selects the source table: `1m`→readings_1m, `15m`→readings_15m,
 * `daily`→readings_daily. For 1m/15m a point is the bucket's aggregate value with its OT
 * quality flag; for `daily` the point value is the day's kWh and `quality` is null (a daily
 * aggregate has no single quality flag). `ts` is always epoch-ms at the bucket/day start so
 * the client plots one numeric x-axis regardless of resolution.
 */

/** One series point: epoch-ms bucket start + value, with the OT quality flag (null for daily). */
export const SeriesPointSchema = z.object({
  /** Epoch ms at bucket start (1m/15m) or 00:00 of the day (daily). */
  ts: z.number(),
  value: z.number(),
  /** OT data-quality flag (§8.3); null for daily aggregates. */
  quality: QualitySchema.nullable(),
});
export type SeriesPoint = z.infer<typeof SeriesPointSchema>;

export const SeriesResponseSchema = z.object({
  tagId: z.string(),
  /** Tag display name from the dictionary (e.g. "Incomer Active Power"). */
  name: z.string(),
  /** Engineering unit (kW, kWh, …). For `daily` the plotted value is kWh. */
  engUnit: z.string(),
  resolution: ResolutionSchema,
  /** Inclusive window start (ISO-8601), echoed back as resolved by the server. */
  from: z.string().datetime(),
  /** Exclusive window end (ISO-8601). */
  to: z.string().datetime(),
  points: z.array(SeriesPointSchema),
  /** True until real metered data backs the window — UI shows the illustrative chip. */
  illustrative: z.boolean(),
});
export type SeriesResponse = z.infer<typeof SeriesResponseSchema>;
